// The leaderboard as the table renders it: one row per miner, every value already
// decided. Pure - the table does no arithmetic and no lookups of its own.
import type { SweMinerListItem } from "@/lib/api/types";
import { compareCategories } from "@/lib/dashboard/categories";
import { scaleOf, type Scale } from "@/lib/dashboard/scale";
import { screenerStage, stageVerdict, type StageVerdict } from "@/lib/dashboard/screener";

export interface StageCell {
  score: number | null;
  /** Passed, failed, still being measured, or never run - four states, never collapsed. */
  verdict: StageVerdict;
}

export interface LeaderboardRow {
  hotkey: string;
  /** Position in the current ordering, 1-based. Not the backend's rank. */
  place: number;
  overall: number | null;
  stage1: StageCell;
  stage2: StageCell;
  /** Keyed by category; a missing key means the category was not measured for this miner. */
  categories: Record<string, number | null>;
  lastSubmit: string | null;
  status: string | null;
}

/**
 * The category columns for this competition: every key any miner carries, in difficulty
 * order. Derived from the data because the set changes between competitions.
 */
export function categoryColumns(miners: SweMinerListItem[]): string[] {
  const keys = new Set<string>();
  for (const m of miners) {
    for (const key of Object.keys(m.category_scores ?? {})) keys.add(key);
  }
  return [...keys].sort(compareCategories);
}

export interface StageClock {
  /** The competition is still producing numbers. */
  live: boolean;
  stage1Started: boolean;
  stage2Started: boolean;
}

const SETTLED: StageClock = { live: false, stage1Started: true, stage2Started: true };

export function toRows(
  miners: SweMinerListItem[],
  columns: string[],
  clock: StageClock = SETTLED,
): LeaderboardRow[] {
  return miners.map((m, i) => {
    const s1 = screenerStage(m.screener, 1);
    const s2 = screenerStage(m.screener, 2);
    // Per-category numbers only count for a miner that was actually scored. The payload
    // carries them for a failed-review or not-qualified miner too, but those runs were
    // thrown out - printing 0.557 next to "FAILED REVIEW" reads as a result that stands.
    // The production dashboard has always hidden them; this keeps the two agreeing.
    const scored = m.status === "scored";
    const categories: Record<string, number | null> = {};
    for (const key of columns) categories[key] = scored ? (m.category_scores?.[key] ?? null) : null;

    return {
      hotkey: m.hotkey,
      place: i + 1,
      overall: m.total_score,
      // The legacy top-level pass belongs to stage 1; stage 2 has no legacy form.
      stage1: {
        score: s1?.score ?? m.screener_score ?? null,
        verdict: stageVerdict(s1 ?? { score: m.screener_score ?? null, screener_passed: m.screener_passed ?? null }, {
          started: clock.stage1Started,
          live: clock.live,
        }),
      },
      stage2: { score: s2?.score ?? null, verdict: stageVerdict(s2, { started: clock.stage2Started, live: clock.live }) },
      categories,
      lastSubmit: m.last_submit,
      status: m.status,
    };
  });
}

/** One scale per numeric column, computed over the whole board rather than the page. */
export interface LeaderboardScales {
  overall: Scale | null;
  stage1: Scale | null;
  stage2: Scale | null;
  categories: Record<string, Scale | null>;
}

export function leaderboardScales(rows: LeaderboardRow[], columns: string[]): LeaderboardScales {
  const categories: Record<string, Scale | null> = {};
  for (const key of columns) categories[key] = scaleOf(rows.map((r) => r.categories[key]));
  return {
    overall: scaleOf(rows.map((r) => r.overall)),
    stage1: scaleOf(rows.map((r) => r.stage1.score)),
    stage2: scaleOf(rows.map((r) => r.stage2.score)),
    categories,
  };
}

/**
 * Who wears the crown. Only a scored miner can hold it: the board also carries higher
 * totals from miners whose run was thrown out, and crowning one of those would name a
 * winner the competition did not have.
 */
/**
 * The best score in a competition and whose it is - among scored miners only, the same
 * rule as the crown in the table. A miner thrown out in review keeps a raw total_score
 * (0.509 on CoT-Compression-9), and taking the plain maximum put that figure in the
 * "Top score" tile while the crown sat on 0.085 a few rows down. Production shows 0.085.
 */
export function topScoredMiner(miners: SweMinerListItem[]): { hotkey: string; score: number } | null {
  let best: { hotkey: string; score: number } | null = null;
  for (const m of miners) {
    if (m.status !== "scored" || typeof m.total_score !== "number") continue;
    if (best === null || m.total_score > best.score) best = { hotkey: m.hotkey, score: m.total_score };
  }
  return best;
}

export function topScoredHotkey(rows: LeaderboardRow[]): string | null {
  let best: LeaderboardRow | null = null;
  for (const row of rows) {
    if (row.status !== "scored" || typeof row.overall !== "number") continue;
    if (best === null || row.overall > (best.overall ?? Number.NEGATIVE_INFINITY)) best = row;
  }
  return best?.hotkey ?? null;
}
